import { DataProvider, EditStack } from "@acadia-ui/theme";
import { TreeNode } from "@acadia-ui/checkbox";

export class CustomDataProvider extends DataProvider {
	static getCustomTree = (tableId, dataTableApi) => {
		const selectAllState = dataTableApi?.current?.rowSelectionManager?.tree?.selectionState || false;

		// prettier-ignore
		const customTree = new TreeNode(
			"Select All",
			`${tableId}-select-all`,
			selectAllState
		);
		customTree.children = {
			loadedNodes: dataTableApi?.current?.dataProvider?.data?.map((_, i) => {
				// prettier-ignore
				const { selectionState = false, renderProps = {} } =
					TreeNode.getNodeAtPath(
						dataTableApi?.current?.rowSelectionManager?.tree,
						[
                            `${tableId}-select-all`, 
                            `${tableId ? tableId + "-" : ""}row-${i}`
                        ]
					) || {};

				// prettier-ignore
				const levelOne = new TreeNode(
					`Row: ${i}`,
					`${tableId ? tableId + "-" : ""}row-${i}`,
					selectAllState === true ? selectAllState : selectionState,
					customTree,
                    renderProps
				);

				levelOne.children = {
					loadedNodes: dataTableApi?.current?.dataProvider?.childDataProviders?.[i]?.data?.map((_, j) => {
						// prettier-ignore
						const { selectionState = false, renderProps = {} } =
                            TreeNode.getNodeAtPath(
								dataTableApi?.current?.rowSelectionManager?.tree,
								[
									`${tableId}-select-all`,
									`${tableId ? tableId + "-" : ""}row-${i}`,
									`${tableId ? tableId + "-" : ""}row-${i}-group-${j}`
								]
							) || {};

						// prettier-ignore
						return new TreeNode(
							`Grouped Row: ${j}`,
							`${tableId ? tableId + "-" : ""}row-${i}-group-${j}`,
							selectAllState === true ? selectAllState : selectionState,
							levelOne,
                            renderProps
						);
					})
				};
				return levelOne;
			}),
			totalAvailableNodes: dataTableApi?.current?.dataProvider?.data?.length
		};

		return customTree;
	};

	constructor({ id, requestData, archive, updateCheckboxes, ...rest }) {
		super(rest);
		this.id = id;
		this.requestData = requestData;
		this.archive = archive;
		this.rowsPerPage = 20;
		this.updateCheckboxes = updateCheckboxes;
		this.abortController = null;
	}

	doSort(...args) {
		const [_a, _b, sortProp, sortDir] = args;
		if (this.childDataProviders?.length > 0) {
			this.childDataProviders.forEach((childDataProvider, i) => {
				// prettier-ignore
				childDataProvider?.update(
                    childDataProvider.data.sort(
                        (a, b) => super.doSort(a, b, sortProp, sortDir)
                    )
                );
			});
		}
	}

	// Get data based on params
	async get(params) {
		const { currentPage, rowsPerPage, sortProp, sortDir, searchQuery, filters } = this.resolveParams(params);

		if (sortProp !== "CampaignName_Friendly" && sortProp !== null) {
			this.data.sort((a, b) => this.doSort(a, b, sortProp, sortDir));
			return JSON.parse(JSON.stringify({ totalCount: this.totalRows, rows: this.data }));
		}

		if (this.abortController) {
			this.abortController.abort();
		}

		this.abortController = new AbortController();

		try {
			const res = await this.requestData({
				page: currentPage + 1,
				archive: this.archive,
				search: searchQuery || "",
				filters: filters || {},
				rowsPerPage,
				sortProp,
				sortDir,
				abortController: this.abortController
			});

			const { data, totalPrimaryCampaignsCount, totalActivationCampaignsCount } = res;
			const response = this.getResponse(data, totalPrimaryCampaignsCount, totalActivationCampaignsCount);

			this.data.splice(currentPage * rowsPerPage, rowsPerPage, ...response.rows);

			if (sortProp === "CampaignName_Friendly") {
				this.data.sort((a, b) => this.doSort(a, b, sortProp, sortDir));
			}

			setTimeout(this.updateCheckboxes, 0);
			return response;
		} catch (e) {
			if (this.abortController.signal.aborted) {
				console.log("aborted request");
			}
		}
	}

	getResponse(pageRows, totalPrimaryCampaignsCount, totalActivationCampaignsCount) {
		pageRows = pageRows.map((datum) => {
			return {
				...datum,
				CampaignID: datum?.MasterCampaignID,
				CampaignName_Friendly: datum?.MasterCampaignName
			};
		});

		this.editStack = new EditStack(pageRows);
		this.totalRows = totalPrimaryCampaignsCount;
		this.totalPrimaryCampaignsCount = totalPrimaryCampaignsCount;
		this.totalActivationCampaignsCount = totalActivationCampaignsCount;
		this.currentData = pageRows;

		this.childDataProviders.splice(this.currentPage * this.rowsPerPage, this.rowsPerPage, ...this.buildChildDataProviders(pageRows));

		return {
			rows: pageRows,
			totalPrimaryCampaignsCount,
			editStack: this.editStack
		};
	}

	buildChildDataProviders(pageRows) {
		return pageRows.map(({ ActivationCampaigns, ArchivedCampaigns }) => {
			const ChildCampaigns = this.archive ? ArchivedCampaigns : ActivationCampaigns;

			if (ChildCampaigns && Object.values(ChildCampaigns)?.some((arr) => arr?.length > 0)) {
				const childData = Object.entries(ChildCampaigns)?.reduce((curr, [PlatformName, platformCampaigns]) => {
					return [...curr, ...platformCampaigns?.map((campaign) => ({ ...campaign, PlatformName }))];
				}, []);
				const cdp = new DataProvider({ data: childData });
				return cdp;
			}
			const cdp = new DataProvider({ data: [] });
			return cdp;
		});
	}

	resetExpandedAccordionsVisually = () => {
		const accordionControllers = document.querySelectorAll(`[accordioncontrolgroupname=${this.id}__open]`) || [];
		for (const controller of accordionControllers) {
			controller.click();
		}
	};
}
